<template>
  <div>
    <content-placeholder v-if="fetching">
      <el-card>
        <p class="text text-75"></p>
        <p class="text text-full"></p>
        <p class="text text-50"></p>
        <p class="text text-50"></p>
        <p class="text text-50"></p>
      </el-card>
    </content-placeholder>

    <el-card v-else>
      <div slot="header">Status</div>
      <div slot="actions">
        <el-tag size="small" :type="setType" effect="dark">{{ setValue }}</el-tag>
      </div>
      <table cellpadding="5" cellspacing="5">
        <tr>
          <td width="150px"><i class="global-icon fas fa-user"></i> Name</td>
          <td>
            {{ userName(currentUser) }}
          </td>
        </tr>
        <tr>
          <td width="150px"><i class="global-icon fas fa-calendar-week"></i> Week</td>
          <td>{{ weekNumber }}</td>
        </tr>
        <tr>
          <td width="175px"><i class="global-icon far fa-stopwatch"></i> Total Paid Time</td>
          <td>{{ totalPaidTime | formatDiff }} ({{totalPaidEntries}} entries)</td>
        </tr>
        <tr>
          <td width="175px"><i class="global-icon far fa-stopwatch"></i> Total Worked</td>
          <td>{{ totalWorkedTime | formatDiff }} ({{totalWorkedEntries}} entries)</td>
        </tr>
        <tr v-if="timesheet">
          <td width="175px"><i class="global-icon far fa-stopwatch"></i> Total BMS Logged</td>
          <td>{{ timesheet.bms_hours | formatDiff('hours')}}</td>
        </tr>
        <tr>
          <td width="175px"><i class="global-icon far fa-stopwatch"></i> Total S/W</td>
          <td>{{ totalSicknessTime | formatDiff }} ({{totalSicknessEntries}} entries)</td>
        </tr>
        <tr>
          <td width="175px"><i class="global-icon far fa-stopwatch"></i> Total PTO</td>
          <td>{{ totalPTOTime | formatDiff }} ({{totalPTOEntries}} entries)</td>
        </tr>
        <tr>
          <td width="175px"><i class="global-icon far fa-stopwatch"></i> Total Federal</td>
          <td>{{ totalFederalTime | formatDiff }} ({{totalFederalEntries}} entries)</td>
        </tr>
        <tr>
          <td width="175px"><i class="global-icon far fa-stopwatch"></i> Total Unpaid Time</td>
          <td>{{ totalUnpaidTime | formatDiff }} ({{totalUnpaidEntries}} entries)</td>
        </tr>
        <tr>
          <td width="150px"><i class="global-icon fas fa-calendar-day"></i> Start Date</td>
          <td>{{ startOfWeek | formatTime('dddd, MMMM Do YYYY') }}</td>
        </tr>
        <tr>
          <td width="150px"><i class="global-icon fas fa-calendar-day"></i> End Date</td>
          <td>{{ endOfWeek | formatTime('dddd, MMMM Do YYYY') }}</td>
        </tr>
        <tr v-if="timesheet">
          <td width="150px"><i class="global-icon fas fa-user-check"></i> Submitted</td>
          <td>{{ timesheet.created_at | formatTime('MMMM DD, YYYY hh:mm a') }}</td>
        </tr>
        <tr v-if="timesheet">
          <td width="150px"><i class="global-icon fas fa-user-check"></i> Utilization / Efficiency</td>
          <td>{{ efficiency_percentage.toFixed(2) }} %</td>
        </tr>
      </table>
      <div v-if="policyHalt">
        <br>
        <el-alert
          title=""
          type="error"
          :closable="false"
          show-icon>
          <b>Policy not accepted</b>
          <p class="el-alert__description">
            In order to submit your timesheet, you first have to accept the latest changes to the company policy.
          </p>
          <div>
          </div>
        </el-alert>
      </div>
      <div slot="footer" v-if="submittable">
        <el-button class="full-width" type="primary" @click="submitForm()">{{ $t('buttons.submit') }} timesheet</el-button>
      </div>
    </el-card>
  </div>
</template>

<script>
  import moment from 'moment';
  import { mapGetters, mapActions } from 'vuex';
  import timeLogDurationMixin from '@/store/modules/timeLog/mixins/duration';
  import ptoDurationMixin from '@/store/modules/pto/mixins/duration';
  import { showSuccessMessage, showErrorMessage } from '@/ui/message';

  export default {
    name: 'TimesheetOverview',
    props: {
      week: {
        type: [Object, Number, String],
        required: true,
      },
      year: {
        type: [Object, Number],
        required: true,
      },
      timesheet: {
        type: [Object],
      },
      userId: {
        type: Number,
        required: false,
      },
    },
    data() {
      return {
        formData: {
          note: '',
          status: '',
        },
      };
    },
    mixins: [timeLogDurationMixin, ptoDurationMixin],
    computed: {
      ...mapGetters('entities/users', ['currentUser']),
      ...mapGetters('entities/timeLogs', ['fetching']),

      totalTime() {
        return this.timeLogsDuration(this.timeLogs);
      },
      policy() {
        return this.$store.getters['entities/policies/query']().first();
      },
      weekNumber() {
        return this.week;
      },
      startOfISOWeek() {
        return moment(`${this.week} ${this.year}`, 'WW gggg').startOf('isoWeek');
      },
      startOfWeek() {
        return this.getSundayStartOfWeek(this.week, this.year);
      },
      endOfWeek() {
        return this.startOfWeek.clone().endOf('week');
      },
      timeLogs() {
        if (!this.currentUser && !this.userId) {
          return [];
        }

        const currentUserId = this.userId || this.currentUser.id;
        return this.$store.getters['entities/timeLogs/query']()
          .with('category')
          .where(record => record.stopped_at !== null)
          .where(record => moment(record.started_at).isBetween(this.startOfWeek, this.endOfWeek) && record.user_id === currentUserId)
          .where('deleted', false)
          .orderBy('id', 'asc')
          .get();
      },
      efficiency_percentage() {
        const hours = moment.duration(this.timeLogsDuration(this.filterTimeByeCategory('billing'))).asHours();
        if (hours <= 0) {
          return 0;
        }
        return (this.timesheet.bms_hours / hours) * 100;
      },
      totalPaidTime() {
        return this.timeLogsDuration(this.timeLogs.filter(timeLog => timeLog.category.name !== 'Unpaid'));
      },
      totalWorkedTime() {
        return this.timeLogsDuration(this.filterTimeByeCategory('billing'));
      },
      totalSicknessTime() {
        return this.timeLogsDuration(this.filterTimeByeCategory('sickness'));
      },
      totalPTOTime() {
        return this.timeLogsDuration(this.filterTimeByeCategory('pto'));
      },
      totalFederalTime() {
        return this.timeLogsDuration(this.filterTimeByeCategory('holiday'));
      },
      totalUnpaidTime() {
        return this.timeLogsDuration(this.timeLogs.filter(timeLog => timeLog.category.name === 'Unpaid'));
      },

      totalPaidEntries() {
        const paidEntries = this.timeLogs.filter(timeLog => timeLog.category.name !== 'Unpaid');
        return paidEntries ? paidEntries.length : 0;
      },

      totalUnpaidEntries() {
        const unpaidEntries = this.timeLogs.filter(timeLog => timeLog.category.name === 'Unpaid');
        return unpaidEntries ? unpaidEntries.length : 0;
      },
      totalWorkedEntries() {
        return this.filterTimeByeCategory('billing').length;
      },
      totalSicknessEntries() {
        return this.filterTimeByeCategory('sickness').length;
      },
      totalPTOEntries() {
        return this.filterTimeByeCategory('pto').length;
      },
      totalFederalEntries() {
        return this.filterTimeByeCategory('holiday').length;
      },
      policyHalt() {
        return this.currentUser && !this.currentUser.policy_accepted_at && this.policy;
      },
      submittable() {
        if (this.timesheet) {
          switch (this.timesheet.status) {
            case 'approved':
              return false;
            case 'rejected':
              return true;
            case 'pending':
              return false;
            default:
              return true;
          }
        }
        if (this.policyHalt) {
          return false;
        }
        if (this.totalEntries === 0) {
          return false;
        }
        return true;
      },
      setType() {
        if (this.timesheet) {
          switch (this.timesheet.status) {
            case 'approved':
              return 'success';
            case 'rejected':
              return 'danger';
            case 'pending':
              return 'warning';
            default:
              return 'info';
          }
        }
        return 'info';
      },
      setValue() {
        if (this.timesheet) {
          switch (this.timesheet.status) {
            case 'approved':
              return 'Approved';
            case 'rejected':
              return 'Rejected';
            case 'pending':
              return 'Pending';
            default:
              return 'Not Submitted';
          }
        }
        return 'Not Submitted';
      },
      totalEntries() {
        return this.timeLogs ? this.timeLogs.length : 0;
      },
    },
    methods: {
      ...mapActions('entities/policies', ['fetchPolicy']),
      ...mapActions('entities/ptos', ['fetchPtos']),
      ...mapActions('entities/timesheets', ['changeStatus']),
      getSundayStartOfWeek(week, year) {
        const firstSunday = moment({ year }).startOf('year').startOf('week'); // Sunday on or before Jan 1
        return firstSunday.add(week - 1, 'weeks');
      },
      filterTimeByeCategory(category) {
        return this.timeLogs.filter(timeLog => this.getMappedCategory(timeLog.category) === category) || [];
      },
      getMappedCategory(category) {
        let type = category.type.toLowerCase().split('category')[0];
        if (type === 'pto') {
          const categoryName = category.name.toLowerCase();
          if (categoryName.includes('sickness')) {
            type = 'sickness';
          } else if (categoryName.includes('unpaid')) {
            type = 'unpaid';
          }
        }
        return type;
      },
      userName(user) {
        if (!user) {
          return '';
        }
        return (user.profile) ? `${user.profile.first_name} ${user.profile.last_name}` : user.email;
      },

      submitForm() {
        this.$confirm('Do you really want to submit this timesheet?', 'Warning', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          title: 'Confirm',
        }).then(() => {
          if (this.timesheet) {
            const data = { status: 'pending' };
            this.changeStatus({ id: this.timesheet.id, data })
              .then((response) => {
                if (response.status) {
                  showSuccessMessage('Timesheet has been resubmitted successfully');
                } else {
                  showErrorMessage(response.error);
                }
              });
          } else {
            this.$store.dispatch('entities/timesheets/createTimesheet', { ...this.formData, week: this.week, year: this.year, status: 'pending' })
              .then((response) => {
                if (response.status) {
                  showSuccessMessage('Timesheet has been submitted successfully');
                } else {
                  showErrorMessage(response.error);
                }
              });
          }
        });
      },
    },
  };
</script>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/timesheet/TimesheetOverview.vue